import { Form, InputNumber, Checkbox, Button, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faUtensils,
  faDollarSign,
  faGear,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import "./CompTierCard.scss";
import { formatNumberKMBT } from "pages/influencers/InfluencerCard";

interface Props {
  id: string;
  color?: string;
  field?: any;
  loading?: boolean;
  defaultTier?: boolean;
  expanded?: boolean;
  name?: string;
  disableCashComp?: boolean;
  onToggle?: () => void;
  onRemove?: () => void;
  onApply?: () => void;
  onEnabledChange?: (enabled: boolean) => void;
}

const CompTierCard: React.FC<Props> = ({
  id,
  color = "#ddd",
  field,
  loading = false,
  expanded = true,
  defaultTier = false,
  name,
  disableCashComp = false,
  onToggle,
  onRemove,
  onApply,
  onEnabledChange,
}) => {
  const baseName =
    field && field.name !== undefined ? [field.name] : ["tiers", id];

  return (
    <div
      className="comp-tier-card"
      style={{ border: `2px solid ${color === "#fff" ? "#d9d9d9" : color}` }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
          <Form.Item
            name={[...baseName, "enabled"]}
            valuePropName="checked"
            noStyle
          >
            <input
              type="checkbox"
              id={`${name}_${baseName[0]}`}
              name={`${name}_${baseName[0]}`}
              onChange={(event) => onEnabledChange?.(event.target.checked)}
              style={{
                cursor: "pointer",
                accentColor: color === "#fff" ? "#0275ff" : color,
                width: 18,
              }}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const minFollower = getFieldValue([
                name,
                ...baseName,
                "minFollowersAmount",
              ]);
              const maxFollower = getFieldValue([
                name,
                ...baseName,
                "maxFollowersAmount",
              ]);
              const bc = getFieldValue([name, ...baseName, "foodCompAmount"]);
              // const minCashComp = getFieldValue([
              //   name,
              //   ...baseName,
              //   "minCashCompAmount",
              // ]);
              const maxCashComp = getFieldValue([
                name,
                ...baseName,
                "maxCashCompAmount",
              ]);

              const tierName = getFieldValue([name, ...baseName, "name"]);

              return (
                <label
                  htmlFor={`${name}_${baseName[0]}`}
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <div
                    style={{
                      fontSize: 17,
                      maxWidth: 190,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={tierName}
                  >
                    {tierName}
                  </div>

                  <div className="comp-meta">
                    <span>
                      <FontAwesomeIcon icon={faUsers} />{" "}
                      <span style={{ color: "#666" }}>
                        {`${formatNumberKMBT(
                          minFollower,
                          0,
                        )}-${formatNumberKMBT(maxFollower, 0)}`}
                      </span>
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faUtensils} />{" "}
                      <span style={{ color: "#666" }}>
                        {bc ? String(bc) : "-"}
                      </span>
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faDollarSign} />{" "}
                      <span style={{ color: "#666" }}>
                        {`${formatNumberKMBT(maxCashComp, 0)}`}
                      </span>
                    </span>
                  </div>
                </label>
              );
            }}
          </Form.Item>
        </div>

        <div style={{ display: "flex", gap: 2 }}>
          {onToggle && (
            <button
              type="button"
              onClick={onToggle}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              className="hover-orange"
            >
              <FontAwesomeIcon icon={faGear} />
            </button>
          )}
          {!defaultTier && (
            <button
              type="button"
              onClick={onRemove}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              className="hover-red"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          {!defaultTier && (
            <div style={{ width: "100%", marginTop: 12 }}>
              <Form.Item
                name={[...baseName, "name"]}
                label={
                  <span style={{ fontSize: 12, color: "#666" }}>Name</span>
                }
              >
                <Input
                  className="comp-input"
                  disabled={loading}
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    height: "44px",
                    boxShadow: "none",
                    backgroundColor: "#f9fafb",
                    fontSize: 14,
                  }}
                />
              </Form.Item>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginTop: 12,
            }}
          >
            <Form.Item
              name={[...baseName, "minFollowersAmount"]}
              // fieldKey={
              //   baseFieldKey
              //     ? [...baseFieldKey, "minFollowersAmount"]
              //     : undefined
              // }
              label={
                <span style={{ fontSize: 12, color: "#666" }}>
                  Min Followers
                </span>
              }
            >
              <InputNumber
                className="comp-input"
                style={{ width: "100%" }}
                disabled={loading}
                formatter={(value) =>
                  value
                    ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    : ""
                }
                parser={(value) => (value ? value.replace(/,/g, "") : "")}
                min={1000 as any}
              />
            </Form.Item>

            <Form.Item
              name={[...baseName, "maxFollowersAmount"]}
              label={
                <span style={{ fontSize: 12, color: "#666" }}>
                  Max Followers
                </span>
              }
            >
              <InputNumber
                className="comp-input"
                style={{ width: "100%" }}
                disabled={loading}
                formatter={(value) =>
                  value
                    ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    : ""
                }
                parser={(value) => (value ? value.replace(/,/g, "") : "")}
              />
            </Form.Item>
          </div>

          <div style={{ width: "50%", marginTop: 12 }}>
            <Form.Item
              name={[...baseName, "minEngagement"]}
              label={
                <span style={{ fontSize: 12, color: "#666" }}>
                  Min Engagement
                </span>
              }
            >
              <InputNumber
                className="comp-input"
                disabled={loading}
                formatter={(value) =>
                  value
                    ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    : ""
                }
                parser={(value) => (value ? value.replace(/,/g, "") : "")}
                prefix={<span style={{ fontSize: 14 }}>%</span>}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          <div style={{ height: 1, background: "#eee", margin: "12px 0" }} />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              alignItems: "end",
            }}
          >
            <Form.Item
              name={[...baseName, "foodCompAmount"]}
              label={
                <span style={{ fontSize: 12, color: "#666" }}>
                  Max Experience Credit
                </span>
              }
            >
              <InputNumber
                className="comp-input"
                disabled={loading}
                formatter={(value) =>
                  value !== undefined && value !== null
                    ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    : ""
                }
                parser={
                  ((value) => {
                    if (!value) return 0;
                    const parsed = value.replace(/,/g, "");
                    return parseFloat(parsed) || 0;
                  }) as (value: string | undefined) => number
                }
                style={{ width: "100%" }}
                prefix={<span style={{ fontSize: 14 }}>$</span>}
                min={id?.includes("custom_") ? 0 : 75}
              />
            </Form.Item>

            <Form.Item
              name={[...baseName, "guestAllowed"]}
              noStyle
              valuePropName="checked"
            >
              <Checkbox
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  justifyContent: "center",
                  width: "100%",
                  marginBottom: 12,
                }}
                className="custom-checkbox"
              >
                <span
                  style={{
                    fontSize: 12,
                    color: "#666",

                    lineHeight: 1,
                  }}
                >
                  +1 Guest Allowed
                </span>
              </Checkbox>
            </Form.Item>

            {/* <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#888", fontSize: 12 }}>
                <FontAwesomeIcon icon={faCircleInfo} /> Local Benchmark
              </span>
            </div> */}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginTop: 12,
            }}
          >
            {/* <Form.Item
              name={[...baseName, "minCashCompAmount"]}
              label={
                <span
                  style={{
                    fontSize: 12,
                    color: "#666",

                    display: "block",
                  }}
                >
                  Min Cash
                </span>
              }
            >
              <InputNumber
                className="comp-input"
                disabled={loading || disableCashComp}
                formatter={(value) =>
                  value
                    ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    : ""
                }
                parser={(value) => (value ? value.replace(/,/g, "") : "")}
                style={{ width: "100%" }}
                prefix={<span style={{ fontSize: 14 }}>$</span>}
              />
            </Form.Item> */}
            <Form.Item
              name={[...baseName, "maxCashCompAmount"]}
              label={
                <span style={{ fontSize: 12, color: "#666" }}>
                  Max Cash Offer
                </span>
              }
            >
              <InputNumber
                className="comp-input"
                disabled={loading || disableCashComp}
                formatter={(value) =>
                  value
                    ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    : ""
                }
                parser={(value) => (value ? value.replace(/,/g, "") : "")}
                style={{ width: "100%" }}
                prefix={<span style={{ fontSize: 14 }}>$</span>}
              />
            </Form.Item>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 12,
            }}
          >
            <Button
              type="primary"
              onClick={() => onApply && onApply()}
              className="comp-apply-btn"
              size="small"
              shape="round"
              style={{ padding: 16, color: "#fff", fontSize: 16 }}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompTierCard;
